import { useState, useEffect, useRef } from 'react';
import { ticketsAPI, aiAPI, usersAPI, rmmAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDate, formatRelativeTime, priorityColors, statusColors, statusLabels, getSLAStatus, formatSLADue, getInitials, surfaceAssignmentEmailResultWith } from '@/lib/utils';
import { formatMst, utcIsoToMstLocal, mstLocalToUtcIso } from '@/lib/timezone';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
// Tabs/ScrollArea no longer used after switch to single-scroll layout.
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
// Tabs primitive replaced with anchor-based single-scroll layout (Apr 2026).
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import imageCompression from 'browser-image-compression';
import WorkOrderForm from './WorkOrderForm';
import TierElevationDialog from './TierElevationDialog';
import {
  X,
  Send,
  Sparkles,
  Wand2,
  FileText,
  User,
  Clock,
  Tag,
  Building2,
  Loader2,
  Users,
  Pencil,
  MessageSquare,
  Lock,
  AlertCircle,
  ImagePlus,
  Camera,
  Wrench,
  MapPin,
  Home,
  Calendar as CalendarIcon,
  Activity,
  Paperclip,
  Link as LinkIcon,
  ShieldCheck,
  ScreenShare,
} from 'lucide-react';
import { RemoteDesktop } from './rmm/RemoteDesktop';

function TicketAIAssist({ ticketId }) {
  const [out, setOut] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = () => {
    setLoading(true);
    rmmAPI.aiTicketAssist(ticketId).then((r) => setOut(r.data)).catch((e) => toast.error(e.response?.data?.detail || 'AI assist failed')).finally(() => setLoading(false));
  };
  return (
    <div data-testid="ticket-ai-assist">
      <Button size="sm" variant="outline" className="rounded-full text-violet-600 border-violet-200 hover:bg-violet-50" onClick={run} disabled={loading} data-testid="ticket-ai-assist-btn">
        {loading ? 'Analyzing…' : '✦ AI Assist'}
      </Button>
      {out && (
        <div className="mt-2 p-3 rounded-xl border border-violet-200 bg-violet-50/50 text-xs text-slate-700 whitespace-pre-wrap" data-testid="ticket-ai-assist-output">
          <Badge className="bg-violet-100 text-violet-700 mb-1.5">AI-generated suggestions · you decide</Badge>
          <p>{out.summary}</p>
        </div>
      )}
    </div>
  );
}

// Shows the RMM device linked to this ticket (when created from an alert) and
// lets an admin launch a Remote Control session straight from the ticket.
function TicketRmmDevice({ ticket, isAdmin }) {
  const [device, setDevice] = useState(null);
  const [remoteOpen, setRemoteOpen] = useState(false);
  useEffect(() => {
    if (!ticket.rmm_device_id) return;
    rmmAPI.getDevice(ticket.rmm_device_id).then((r) => setDevice(r.data)).catch(() => setDevice(null));
  }, [ticket.rmm_device_id]);
  if (!ticket.rmm_device_id) return null;
  const online = device?.status === 'online';
  return (
    <div className="p-3 rounded-xl border border-cyan-200 bg-cyan-50/40 flex items-center justify-between gap-3 flex-wrap" data-testid="ticket-rmm-device">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <ScreenShare className="w-4 h-4 text-cyan-600" />
          <span className="text-sm font-semibold text-slate-800">{device?.hostname || ticket.rmm_device_hostname || 'Linked RMM device'}</span>
          {device && <Badge className={online ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{device.status}</Badge>}
        </div>
        <p className="text-xs text-slate-500 mt-0.5">
          {device ? `${device.customer_name || ''}${device.site_name ? ' · ' + device.site_name : ''} · ${device.os_version || device.os || ''}` : 'Monitored via RMM'}
        </p>
      </div>
      {isAdmin && (
        <Button size="sm" className="rounded-full bg-cyan-600 hover:bg-cyan-700 text-white" disabled={!online} onClick={() => setRemoteOpen(true)}
          title={online ? 'Start a remote control session' : 'Device is offline'} data-testid="ticket-remote-control-btn">
          <ScreenShare className="w-3.5 h-3.5 mr-1" />Remote Control
        </Button>
      )}
      {remoteOpen && device && (
        <RemoteDesktop device={device} ticketId={ticket.id} onClose={() => setRemoteOpen(false)} />
      )}
    </div>
  );
}

export default function TicketDetail({ ticket, onUpdate, onClose }) {  const { user, isAgent, isAdmin } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [aiSummary, setAiSummary] = useState('');
  const [loadingAI, setLoadingAI] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]); // Array of {dataUrl, name}
  const [compressing, setCompressing] = useState(false);
  const [showWorkOrderForm, setShowWorkOrderForm] = useState(false);
  // Admin-only "edit subject + description" dialog
  const [showEditBasicsDialog, setShowEditBasicsDialog] = useState(false);
  const [basicsDraft, setBasicsDraft] = useState({ subject: '', description: '' });
  const [savingBasics, setSavingBasics] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [ticket.id]);

  const prevMsgCount = useRef(null);
  useEffect(() => {
    // Only auto-scroll (inside the ticket pane, never the window) when a new message arrives after initial load.
    const isInitial = prevMsgCount.current === null;
    const grew = !isInitial && messages.length > prevMsgCount.current;
    prevMsgCount.current = messages.length;
    if (isInitial || !grew) return;
    const el = messagesEndRef.current;
    const pane = el?.closest('[data-testid="ticket-detail-scroll"]');
    if (!el || !pane) return;
    const delta = el.getBoundingClientRect().bottom - pane.getBoundingClientRect().bottom;
    pane.scrollTo({ top: pane.scrollTop + delta + 16, behavior: 'smooth' });
  }, [messages]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Fetch both agents and admins so they can all be assigned
      const [messagesRes, agentsRes, adminsRes] = await Promise.all([
        ticketsAPI.getMessages(ticket.id),
        isAgent ? usersAPI.list('agent') : Promise.resolve({ data: [] }),
        isAgent ? usersAPI.list('admin') : Promise.resolve({ data: [] }),
      ]);
      setMessages(messagesRes.data);
      // Merge + de-dupe by id, sort by name
      const merged = [...(agentsRes.data || []), ...(adminsRes.data || [])];
      const seen = new Set();
      const deduped = merged
        .filter((u) => (seen.has(u.id) ? false : (seen.add(u.id), true)))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setAgents(deduped);
    } catch (err) {
      console.error('Failed to load ticket data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && pendingAttachments.length === 0) return;

    try {
      setSending(true);
      await ticketsAPI.addMessage(ticket.id, {
        content: newMessage || '(attachment)',
        is_internal: isInternal,
        attachments: pendingAttachments.map((a) => a.dataUrl),
      });
      setNewMessage('');
      setIsInternal(false);
      setPendingAttachments([]);
      loadData();
      onUpdate();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const compressFile = async (file) => {
    // browser-image-compression: reduce to ~50% size with reasonable max dimension
    const options = {
      maxSizeMB: 1.2, // cap upload size
      maxWidthOrHeight: 1600,
      useWebWorker: true,
      initialQuality: 0.5, // ~50% quality
      fileType: file.type === 'image/png' ? 'image/png' : 'image/jpeg',
    };
    try {
      const compressed = await imageCompression(file, options);
      const dataUrl = await imageCompression.getDataUrlFromFile(compressed);
      return { dataUrl, name: file.name, size: compressed.size };
    } catch (err) {
      console.error('Compression failed, using original:', err);
      const reader = new FileReader();
      return await new Promise((resolve, reject) => {
        reader.onload = () => resolve({ dataUrl: reader.result, name: file.name, size: file.size });
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setCompressing(true);
    try {
      const compressed = await Promise.all(files.map((f) => compressFile(f)));
      setPendingAttachments((prev) => [...prev, ...compressed]);
    } finally {
      setCompressing(false);
      // Reset input so same file can be re-selected
      if (e.target) e.target.value = '';
    }
  };

  const removeAttachment = (idx) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleStatusChange = async (status) => {
    try {
      await ticketsAPI.update(ticket.id, { status });
      onUpdate();
      // When the agent marks a ticket as closed (or resolved), open the
      // Work Order Form so they can document what was done.
      if (isAgent && (status === 'closed' || status === 'resolved')) {
        setShowWorkOrderForm(true);
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handlePriorityChange = async (priority) => {
    try {
      await ticketsAPI.update(ticket.id, { priority });
      onUpdate();
    } catch (err) {
      console.error('Failed to update priority:', err);
    }
  };

  const handleAssign = async (agentId) => {
    try {
      // Treat "unassign" sentinel as null
      const res = await ticketsAPI.update(ticket.id, {
        assigned_agent_id: agentId === '__unassigned' ? null : agentId,
      });
      surfaceAssignmentEmailResultWith(toast, res?.data);
      onUpdate();
    } catch (err) {
      console.error('Failed to assign:', err);
    }
  };

  // Multi-tech: toggle a co-assignee on/off. The full updated list is sent
  // to the backend (PATCH /api/tickets/{id}.assigned_agent_ids).
  const handleToggleCoAssignee = async (agentId) => {
    try {
      const current = (ticket.assigned_agent_ids && ticket.assigned_agent_ids.length)
        ? ticket.assigned_agent_ids
        : (ticket.assigned_agent_id ? [ticket.assigned_agent_id] : []);
      const next = current.includes(agentId)
        ? current.filter((id) => id !== agentId)
        : [...current, agentId];
      const res = await ticketsAPI.update(ticket.id, { assigned_agent_ids: next });
      surfaceAssignmentEmailResultWith(toast, res?.data);
      onUpdate();
    } catch (err) {
      console.error('Failed to update co-assignees:', err);
    }
  };

  const handleClaimSelf = async () => {
    try {
      await ticketsAPI.claim(ticket.id);
      onUpdate();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to claim ticket');
    }
  };

  const [showElevationDialog, setShowElevationDialog] = useState(false);
  const [submittingElevation, setSubmittingElevation] = useState(false);

  const handleRequestElevation = () => setShowElevationDialog(true);

  const handleElevationConfirm = async (payload) => {
    try {
      setSubmittingElevation(true);
      // ticketsAPI.requestElevation(id, reason) — extend with target_tier + priority via raw call
      await ticketsAPI.requestElevation(ticket.id, payload);
      setShowElevationDialog(false);
      onUpdate();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to request elevation');
    } finally {
      setSubmittingElevation(false);
    }
  };

  const handleGetAISuggestion = async () => {
    try {
      setLoadingAI(true);
      const res = await aiAPI.suggestReply(ticket.id);
      setAiSuggestion(res.data.suggestion);
    } catch (err) {
      console.error('Failed to get AI suggestion:', err);
    } finally {
      setLoadingAI(false);
    }
  };

  const handleGetAISummary = async () => {
    try {
      setLoadingSummary(true);
      const res = await aiAPI.summarize(ticket.id);
      setAiSummary(res.data.summary);
    } catch (err) {
      console.error('Failed to get AI summary:', err);
    } finally {
      setLoadingSummary(false);
    }
  };

  const useAISuggestion = () => {
    setNewMessage(aiSuggestion);
    setAiSuggestion('');
  };

  return (
    <div className="flex flex-col h-full bg-slate-50" data-testid="ticket-detail">
      {/* Header */}
      <div className="px-4 md:px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-sm text-slate-500 font-mono">{ticket.ticket_number}</span>
              <Badge className={`${statusColors[ticket.status] || statusColors.open} border`}>
                {statusLabels[ticket.status] || ticket.status.replace('_', ' ')}
              </Badge>
              <Badge className={`${priorityColors[ticket.priority]} border`}>
                {ticket.priority}
              </Badge>
              {ticket.assignment_tier && (
                <Badge
                  variant="outline"
                  className={
                    ticket.assignment_tier === 3
                      ? 'border-purple-300 text-purple-700 bg-purple-50'
                      : ticket.assignment_tier === 2
                      ? 'border-amber-300 text-amber-700 bg-amber-50'
                      : 'border-cyan-300 text-cyan-700 bg-cyan-50'
                  }
                  data-testid="ticket-tier-badge"
                >
                  Tier {ticket.assignment_tier}
                </Badge>
              )}
              {ticket.category && (
                <Badge variant="outline" className="border-slate-200 text-slate-600 text-[11px]">
                  <Tag className="w-3 h-3 mr-1" />{ticket.category}
                </Badge>
              )}
              {ticket.location && (
                <Badge variant="outline" className="border-slate-200 text-slate-600 text-[11px]">
                  <MapPin className="w-3 h-3 mr-1" />{ticket.location}
                </Badge>
              )}
              {ticket.site_name && (
                <Badge variant="outline" className="border-cyan-200 bg-cyan-50 text-cyan-700 text-[11px]" data-testid="ticket-site-badge">
                  <Home className="w-3 h-3 mr-1" />{ticket.site_name}
                </Badge>
              )}
              {ticket.escalation_requested && (
                <Badge className="bg-red-100 text-red-700 border-red-200" data-testid="escalation-badge">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Escalation Requested
                </Badge>
              )}
            </div>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg md:text-xl font-bold text-slate-900 leading-tight" data-testid="ticket-subject">{ticket.subject}</h2>
                {(ticket.site_name || ticket.customer_name || ticket.location || ticket.site_address) && (
                  <p className="mt-1 mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-slate-600" data-testid="ticket-subject-location">
                    <MapPin className="w-3.5 h-3.5 text-cyan-600 shrink-0" />
                    {ticket.site_name && <span className="font-semibold text-cyan-700">{ticket.site_name}</span>}
                    {ticket.customer_name && <span className="text-slate-500">{ticket.site_name ? '· ' : ''}{ticket.customer_name}</span>}
                    {(ticket.site_address || ticket.location) && <span className="text-slate-500">· {ticket.site_address || ticket.location}</span>}
                  </p>
                )}
              </div>
              {isAdmin && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-500 hover:text-cyan-600"
                  onClick={() => {
                    setBasicsDraft({
                      subject: ticket.subject || '',
                      description: ticket.description || '',
                    });
                    setShowEditBasicsDialog(true);
                  }}
                  title="Edit subject / description"
                  data-testid="edit-ticket-basics-btn"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Assignee + Requester quick row */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <div className="flex items-center gap-2 flex-wrap" aria-label="Assigned to">
                <span className="text-slate-500">Assigned to:</span>
                {(() => {
                  // Prefer the multi-tech list; fall back to legacy single field.
                  const ids = ticket.assigned_agent_ids && ticket.assigned_agent_ids.length
                    ? ticket.assigned_agent_ids
                    : (ticket.assigned_agent_id ? [ticket.assigned_agent_id] : []);
                  const names = ticket.assigned_agent_names && ticket.assigned_agent_names.length
                    ? ticket.assigned_agent_names
                    : (ticket.assigned_agent_name ? [ticket.assigned_agent_name] : []);
                  if (!ids.length) {
                    return <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[11px]">Unassigned</Badge>;
                  }
                  return (
                    <span className="inline-flex items-center gap-2 flex-wrap" data-testid="ticket-assignees">
                      {ids.map((id, i) => (
                        <span key={id} className="inline-flex items-center gap-1.5 bg-cyan-50 border border-cyan-200 rounded-full pl-1 pr-2 py-0.5">
                          <Avatar className="w-5 h-5 border border-white">
                            <AvatarFallback className="bg-cyan-100 text-cyan-700 text-[9px] font-semibold">
                              {getInitials(names[i] || '?')}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-xs font-medium text-slate-800">{names[i] || 'Agent'}</span>
                        </span>
                      ))}
                    </span>
                  );
                })()}
              </div>
              {ticket.customer_name && (
                <div className="flex items-center gap-2 min-w-0" aria-label="Requester">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-500">Requester:</span>
                  <strong className="text-slate-800 truncate">{ticket.customer_name}</strong>
                  {ticket.customer_email && (
                    <span className="text-slate-400 truncate hidden md:inline">({ticket.customer_email})</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isAgent && (
              <>
                {ticket.assigned_agent_id === user?.id &&
                  (ticket.assignment_tier || 1) < 3 &&
                  !ticket.escalation_requested && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full border-amber-500 text-amber-600 hover:bg-amber-50 hidden md:inline-flex"
                      onClick={handleRequestElevation}
                      disabled={sending}
                      data-testid="request-elevation-btn"
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Request Elevation
                    </Button>
                  )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                  onClick={() => setShowWorkOrderForm(true)}
                  data-testid="create-work-order-btn"
                  aria-label="Create work order"
                >
                  <Wrench className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">{ticket.ticket_type === 'onsite' ? 'Onsite Work Order' : 'Work Order'}</span>
                </Button>
                {ticket.status === 'closed' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full border-blue-500 text-blue-600 hover:bg-blue-50"
                    onClick={() => handleStatusChange('open')}
                    data-testid="reopen-ticket-btn"
                    aria-label="Reopen ticket"
                  >
                    Reopen
                  </Button>
                )}
              </>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} className="text-slate-400 hover:text-slate-600 min-h-[40px] min-w-[40px]" data-testid="close-ticket-btn" aria-label="Close ticket detail">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Work Order Form Modal */}
      {isAgent && showWorkOrderForm && (
        <WorkOrderForm
          ticket={ticket}
          onClose={() => setShowWorkOrderForm(false)}
          onSaved={() => {
            setShowWorkOrderForm(false);
            onUpdate && onUpdate();
          }}
        />
      )}

      {/* Tier elevation dialog */}
      <TierElevationDialog
        open={showElevationDialog}
        onOpenChange={setShowElevationDialog}
        ticket={ticket}
        onConfirm={handleElevationConfirm}
        submitting={submittingElevation}
      />

      {/* Admin-only edit subject/description dialog */}
      <Dialog open={showEditBasicsDialog} onOpenChange={setShowEditBasicsDialog}>
        <DialogContent className="bg-white border-slate-200 max-w-xl max-h-[90vh] max-h-[90dvh] overflow-y-auto" data-testid="edit-ticket-basics-dialog">
          <DialogHeader>
            <DialogTitle>Edit ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Subject</Label>
              <Input
                value={basicsDraft.subject}
                onChange={(e) => setBasicsDraft((d) => ({ ...d, subject: e.target.value }))}
                className="bg-white border-slate-200"
                data-testid="edit-ticket-subject-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Description</Label>
              <Textarea
                rows={8}
                value={basicsDraft.description}
                onChange={(e) => setBasicsDraft((d) => ({ ...d, description: e.target.value }))}
                className="bg-white border-slate-200"
                data-testid="edit-ticket-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditBasicsDialog(false)}>Cancel</Button>
            <Button
              className="bg-cyan-500 hover:bg-cyan-600 rounded-full"
              disabled={savingBasics || !basicsDraft.subject.trim()}
              onClick={async () => {
                try {
                  setSavingBasics(true);
                  await ticketsAPI.update(ticket.id, {
                    subject: basicsDraft.subject.trim(),
                    description: basicsDraft.description,
                  });
                  setShowEditBasicsDialog(false);
                  onUpdate();
                } catch (err) {
                  alert(err.response?.data?.detail || 'Failed to save changes');
                } finally {
                  setSavingBasics(false);
                }
              }}
              data-testid="edit-ticket-basics-save"
            >
              {savingBasics ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single-scroll page (was Tabs). Header anchor nav scrolls to each
          section; each section is always rendered together so techs can see
          everything at a glance and Cmd+F across all of it. */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b border-slate-200 bg-white px-4 md:px-6 overflow-x-auto sticky top-0 z-10">
          <nav className="flex bg-transparent h-12 items-center gap-1" aria-label="Ticket sections">
            <a href="#sec-overview" data-testid="tab-overview" className="inline-flex items-center rounded-full h-9 px-4 text-sm text-slate-700 hover:bg-slate-100 transition-colors">
              <FileText className="w-4 h-4 mr-1.5" />Overview
            </a>
            <a href="#sec-conversation" data-testid="tab-conversation" className="inline-flex items-center rounded-full h-9 px-4 text-sm text-slate-700 hover:bg-slate-100 transition-colors">
              <MessageSquare className="w-4 h-4 mr-1.5" />Conversation
            </a>
            <a href="#sec-activity" data-testid="tab-activity" className="inline-flex items-center rounded-full h-9 px-4 text-sm text-slate-700 hover:bg-slate-100 transition-colors">
              <Activity className="w-4 h-4 mr-1.5" />Activity
            </a>
            <a href="#sec-attachments" data-testid="tab-attachments" className="inline-flex items-center rounded-full h-9 px-4 text-sm text-slate-700 hover:bg-slate-100 transition-colors">
              <Paperclip className="w-4 h-4 mr-1.5" />Attachments
            </a>
            <a href="#sec-sla" data-testid="tab-sla" className="inline-flex items-center rounded-full h-9 px-4 text-sm text-slate-700 hover:bg-slate-100 transition-colors">
              <ShieldCheck className="w-4 h-4 mr-1.5" />SLA
            </a>
            <a href="#sec-asset" data-testid="tab-asset" className="inline-flex items-center rounded-full h-9 px-4 text-sm text-slate-700 hover:bg-slate-100 transition-colors">
              <LinkIcon className="w-4 h-4 mr-1.5" />Linked Asset
            </a>
          </nav>
        </div>

        <div className="flex-1 flex overflow-hidden">
          <div className="relative flex-1 flex flex-col overflow-y-auto scroll-smooth" data-testid="ticket-detail-scroll">
            {/* OVERVIEW SECTION */}
            <section id="sec-overview" className="p-4 md:p-6 space-y-4 scroll-mt-14">
              {isAgent && <TicketAIAssist ticketId={ticket.id} />}
              {isAgent && (
                <Card className="bg-white border-slate-200 xl:hidden" data-testid="mobile-quick-actions">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-slate-800">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500 uppercase tracking-wide">Status</Label>
                      <Select value={ticket.status} onValueChange={handleStatusChange}>
                        <SelectTrigger className="mt-1 bg-white border-slate-200 h-11" data-testid="mobile-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="pending">Waiting</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 uppercase tracking-wide">Priority</Label>
                      <Select value={ticket.priority} onValueChange={handlePriorityChange}>
                        <SelectTrigger className="mt-1 bg-white border-slate-200 h-11" data-testid="mobile-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent / Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-slate-500 uppercase tracking-wide">Assigned To</Label>
                      {isAdmin ? (
                        <Select value={ticket.assigned_agent_id || '__unassigned'} onValueChange={handleAssign}>
                          <SelectTrigger className="mt-1 bg-white border-slate-200 h-11" data-testid="mobile-assignee-select">
                            <SelectValue placeholder="Unassigned" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__unassigned">Unassigned</SelectItem>
                            {user?.id && ticket.assigned_agent_id !== user.id && (
                              <SelectItem value={user.id}>Assign to me ({user.name})</SelectItem>
                            )}
                            {agents.map((agent) => (
                              <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="mt-1 flex items-center justify-between gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <span className="text-sm text-slate-700">
                            {ticket.assigned_agent_name || <span className="text-amber-700 italic">Unassigned</span>}
                          </span>
                          {!ticket.assigned_agent_id && (
                            <Button type="button" size="sm" className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white" onClick={handleClaimSelf} data-testid="mobile-claim-btn">
                              Claim
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="bg-white border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-slate-800">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-white border-slate-200">
                  <CardContent className="p-4 space-y-3">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Ticket Info</h3>
                    <DefRow label="Ticket ID" value={ticket.ticket_number} mono />
                    <DefRow label="Status" value={<Badge className={`${statusColors[ticket.status] || statusColors.open} border`}>{statusLabels[ticket.status] || ticket.status}</Badge>} />
                    <DefRow label="Priority" value={<Badge className={`${priorityColors[ticket.priority]} border`}>{ticket.priority}</Badge>} />
                    <DefRow label="Category" value={ticket.category || '—'} />
                    <DefRow label="Location" value={ticket.location || '—'} />
                    <DefRow label="Site" value={ticket.site_name ? `${ticket.site_name}${ticket.site_address ? ' — ' + ticket.site_address : ''}` : '—'} />
                    <DefRow label="Department" value={ticket.department_name || '—'} />
                    <DefRow label="Tier" value={ticket.assignment_tier ? `Tier ${ticket.assignment_tier}` : '—'} />
                  </CardContent>
                </Card>
                <Card className="bg-white border-slate-200">
                  <CardContent className="p-4 space-y-3">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">People & Time</h3>
                    <DefRow label="Requester" value={ticket.customer_name || '—'} />
                    <DefRow
                      label="Assigned to"
                      value={
                        ticket.assigned_agent_name ? (
                          <span className="inline-flex items-center gap-2">
                            <Avatar className="w-6 h-6 border border-slate-200">
                              <AvatarFallback className="bg-cyan-100 text-cyan-700 text-[10px] font-semibold">
                                {getInitials(ticket.assigned_agent_name)}
                              </AvatarFallback>
                            </Avatar>
                            <strong className="text-slate-800">{ticket.assigned_agent_name}</strong>
                          </span>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200">Unassigned</Badge>
                        )
                      }
                    />
                    <DefRow label="Created" value={`${formatDate(ticket.created_at)} MST`} />
                    <DefRow label="Updated" value={formatRelativeTime(ticket.updated_at)} />
                    <DefRow label="Scheduled" value={ticket.scheduled_at ? formatMst(ticket.scheduled_at) : '—'} />
                    <DefRow label="SLA Due" value={
                      <span className={getSLAStatus(ticket).state === 'overdue' ? 'text-red-600 font-semibold' : ''}>
                        {ticket.sla_due_at ? `${formatMst(ticket.sla_due_at)} (${formatSLADue(ticket)})` : '—'}
                      </span>
                    } />
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* COMMENTS SECTION (public messages) */}
            <section id="sec-conversation" className="border-t border-slate-100 p-4 md:p-6 space-y-3 scroll-mt-14">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Conversation
                {isAgent && (
                  <span className="ml-2 text-xs text-slate-400 font-normal normal-case">
                    Public replies + internal notes (agents only). Use the toggle below to send an internal note.
                  </span>
                )}
              </h2>
              {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
              ) : (
                <div className="space-y-4" data-testid="conversation-feed">
                  {messages.map((msg) => (
                    <MessageRow key={msg.id} msg={msg} />
                  ))}
                  {messages.length === 0 && (
                    <p className="text-center text-sm text-slate-400 py-8">No messages yet.</p>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
              <ReplyBox
                isInternal={isInternal}
                allowToggleInternal={isAgent}
                {...{ aiSuggestion, setAiSuggestion, useAISuggestion, isAgent, handleGetAISuggestion, loadingAI, setIsInternal, fileInputRef, cameraInputRef, handleFilesSelected, compressing, pendingAttachments, removeAttachment, newMessage, setNewMessage, handleSendMessage, sending }}
              />
            </section>

            {/* ACTIVITY TIMELINE SECTION */}
            <section id="sec-activity" className="border-t border-slate-100 p-4 md:p-6 space-y-3 scroll-mt-14">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                <Activity className="w-4 h-4" /> Activity
              </h2>
              <ActivityTimeline ticket={ticket} messages={messages} />
            </section>

            {/* ATTACHMENTS SECTION */}
            <section id="sec-attachments" className="border-t border-slate-100 p-4 md:p-6 space-y-3 scroll-mt-14">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Attachments
              </h2>
              <AttachmentsTab messages={messages} />
            </section>

            {/* SLA SECTION */}
            <section id="sec-sla" className="border-t border-slate-100 p-4 md:p-6 space-y-3 scroll-mt-14">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> SLA
              </h2>
              <SLATab ticket={ticket} isAgent={isAgent} onUpdate={onUpdate} />
            </section>

            {/* LINKED ASSET SECTION */}
            <section id="sec-asset" className="border-t border-slate-100 p-4 md:p-6 space-y-3 scroll-mt-14">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                <LinkIcon className="w-4 h-4" /> Linked Asset
              </h2>
              <TicketRmmDevice ticket={ticket} isAdmin={isAdmin} />
              <LinkedAssetTab ticket={ticket} isAgent={isAgent} onUpdate={onUpdate} />
            </section>
          </div>

          {/* Sidebar (agents only) */}
          {isAgent && (
            <div className="hidden xl:flex w-72 border-l border-slate-200 p-4 space-y-6 overflow-y-auto bg-white flex-col">
            {/* Status */}
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Status</Label>
              <Select value={ticket.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="mt-2 bg-white border-slate-200" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="pending">Waiting</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Priority */}
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Priority</Label>
              <Select value={ticket.priority} onValueChange={handlePriorityChange}>
                <SelectTrigger className="mt-2 bg-white border-slate-200" data-testid="priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent / Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Assignee */}
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Assigned To</Label>
              {isAdmin ? (
                <>
                  <Select value={ticket.assigned_agent_id || '__unassigned'} onValueChange={handleAssign}>
                    <SelectTrigger className="mt-2 bg-white border-slate-200" data-testid="assignee-select">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unassigned">Unassigned</SelectItem>
                      {user?.id && ticket.assigned_agent_id !== user.id && (
                        <SelectItem value={user.id} data-testid="assign-to-me-option">
                          Assign to me ({user.name})
                        </SelectItem>
                      )}
                      {agents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name}{' '}
                          <span className="text-[10px] text-slate-400 uppercase ml-1">
                            {agent.role}{agent.tier ? ` · T${agent.tier}` : ''}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {ticket.assigned_agent_id !== user?.id && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-2 w-full rounded-full border-cyan-300 text-cyan-600 hover:bg-cyan-50"
                      onClick={() => handleAssign(user.id)}
                      data-testid="assign-self-btn"
                    >
                      Assign to me
                    </Button>
                  )}
                  {/* Multi-tech: pick additional techs to share ownership */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-2 w-full rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 justify-between"
                        data-testid="co-assignees-trigger"
                      >
                        <span className="inline-flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          {(() => {
                            const ids = ticket.assigned_agent_ids || [];
                            const extra = ids.filter((id) => id !== ticket.assigned_agent_id).length;
                            return extra > 0 ? `+${extra} co-assigned tech${extra > 1 ? 's' : ''}` : 'Add co-assigned techs';
                          })()}
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-0 z-[60]" align="end">
                      <div className="p-3 border-b border-slate-100">
                        <p className="text-xs text-slate-500">Tick every tech who shares ownership of this ticket. They'll see it in their queue and can update it.</p>
                      </div>
                      <div className="max-h-64 overflow-y-auto p-2 space-y-1" data-testid="co-assignees-list">
                        {agents.map((agent) => {
                          const ids = (ticket.assigned_agent_ids && ticket.assigned_agent_ids.length)
                            ? ticket.assigned_agent_ids
                            : (ticket.assigned_agent_id ? [ticket.assigned_agent_id] : []);
                          const checked = ids.includes(agent.id);
                          return (
                            <label
                              key={agent.id}
                              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                              data-testid={`co-assignee-row-${agent.id}`}
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => handleToggleCoAssignee(agent.id)}
                                data-testid={`co-assignee-checkbox-${agent.id}`}
                              />
                              <span className="text-sm text-slate-800">{agent.name}</span>
                              <span className="text-[10px] text-slate-400 uppercase ml-auto">
                                {agent.role}{agent.tier ? ` · T${agent.tier}` : ''}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </>
              ) : (
                <div className="mt-2 flex items-center gap-2 justify-between">
                  <span className="text-sm text-slate-700">
                    {ticket.assigned_agent_name || <span className="text-slate-400 italic">Unassigned</span>}
                  </span>
                  {isAgent && !ticket.assigned_agent_id && (
                    <Button
                      type="button"
                      size="sm"
                      className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white"
                      onClick={handleClaimSelf}
                      data-testid="claim-ticket-btn"
                    >
                      Claim Ticket
                    </Button>
                  )}
                </div>
              )}
            </div>

            <Separator className="bg-slate-200" />

            {/* Customer Info */}
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Customer</Label>
              <div className="mt-2 flex items-center gap-3">
                <Avatar className="w-10 h-10 border border-slate-200">
                  <AvatarFallback className="bg-cyan-100 text-cyan-600">{ticket.customer_name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-sm text-slate-800">{ticket.customer_name}</p>
                  <p className="text-xs text-slate-500">{ticket.customer_email}</p>
                </div>
              </div>
            </div>

            {/* Quick Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">Created {formatRelativeTime(ticket.created_at)}</span>
              </div>
              {ticket.scheduled_at && (
                <div className="flex items-center gap-2 text-sm">
                  <CalendarIcon className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Scheduled {formatMst(ticket.scheduled_at)}</span>
                </div>
              )}
              {ticket.location && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{ticket.location}</span>
                </div>
              )}
              {ticket.department_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{ticket.department_name}</span>
                </div>
              )}
              {ticket.tags?.length > 0 && (
                <div className="flex items-start gap-2 text-sm">
                  <Tag className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div className="flex flex-wrap gap-1">
                    {ticket.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs border-slate-200">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Separator className="bg-slate-200" />

            {/* AI Summary */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-slate-500 uppercase tracking-wider">AI Summary</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 text-xs text-cyan-600"
                  onClick={handleGetAISummary}
                  disabled={loadingSummary}
                  data-testid="ai-summary-btn"
                >
                  {loadingSummary ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  Generate
                </Button>
              </div>
              {aiSummary ? (
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{aiSummary}</p>
                </div>
              ) : (
                <p className="text-sm text-slate-400">Click Generate to create an AI summary</p>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== Helper subcomponents =====
function DefRow({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="text-slate-500 min-w-[110px]">{label}</span>
      <span className={`text-slate-800 text-right break-words ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

function MessageRow({ msg }) {
  return (
    <div
      className={`flex gap-3 ${msg.is_internal ? 'bg-amber-50 px-4 py-3 rounded-xl border border-amber-200' : ''}`}
      data-testid={`message-${msg.id}`}
    >
      <Avatar className="w-9 h-9 flex-shrink-0 border border-slate-200">
        <AvatarFallback className="bg-cyan-100 text-cyan-600">{getInitials(msg.sender_name)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="font-medium text-sm text-slate-800">{msg.sender_name}</span>
          <Badge variant="outline" className="text-[10px] capitalize border-slate-200">{msg.sender_role}</Badge>
          {msg.is_internal && (
            <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">
              <Lock className="w-3 h-3 mr-1" />
              Internal
            </Badge>
          )}
          <span className="text-xs text-slate-400">{formatRelativeTime(msg.created_at)}</span>
        </div>
        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{msg.content}</p>
        {msg.attachments && msg.attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2" data-testid={`message-attachments-${msg.id}`}>
            {msg.attachments.map((att, i) => (
              <a
                key={i}
                href={att}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-lg overflow-hidden border border-slate-200 hover:border-cyan-400 transition"
                data-testid={`message-attachment-${msg.id}-${i}`}
              >
                <img src={att} alt={`attachment-${i}`} className="max-h-40 max-w-[220px] object-cover bg-slate-100" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReplyBox({
  forceInternal,
  aiSuggestion, setAiSuggestion, useAISuggestion, isAgent, handleGetAISuggestion, loadingAI,
  isInternal, setIsInternal,
  fileInputRef, cameraInputRef, handleFilesSelected, compressing,
  pendingAttachments, removeAttachment,
  newMessage, setNewMessage, handleSendMessage, sending,
}) {
  return (
    <>
      {aiSuggestion && (
        <div className="p-4 border-t border-slate-200 bg-cyan-50">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-cyan-700">AI Suggested Reply</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setAiSuggestion('')} className="text-slate-500">Dismiss</Button>
                  <Button size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-white" onClick={useAISuggestion} data-testid="use-ai-suggestion-btn">Use This</Button>
                </div>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{aiSuggestion}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-200 bg-white">
        {isAgent && !forceInternal && (
          <div className="flex items-center gap-4 mb-3 flex-wrap">
            <Button type="button" variant="outline" size="sm" className="rounded-full border-cyan-300 text-cyan-600 hover:bg-cyan-50" onClick={handleGetAISuggestion} disabled={loadingAI} data-testid="ai-suggest-btn">
              {loadingAI ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              AI Suggest
            </Button>
            {setIsInternal && (
              <div className="flex items-center gap-2">
                <Checkbox id="internal" checked={isInternal} onCheckedChange={setIsInternal} data-testid="internal-note-checkbox" />
                <Label htmlFor="internal" className="text-sm text-slate-500 cursor-pointer">
                  Internal note (not visible to customer)
                </Label>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFilesSelected} data-testid="attach-file-input" />
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFilesSelected} data-testid="attach-camera-input" />
          <Button type="button" variant="outline" size="sm" className="rounded-full border-slate-300 text-slate-600" onClick={() => fileInputRef.current?.click()} disabled={compressing} data-testid="attach-photo-btn">
            {compressing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-2" />}
            Add Photo
          </Button>
          <Button type="button" variant="outline" size="sm" className="rounded-full border-slate-300 text-slate-600 md:hidden" onClick={() => cameraInputRef.current?.click()} disabled={compressing} data-testid="take-photo-btn">
            <Camera className="w-4 h-4 mr-2" />Camera
          </Button>
          {pendingAttachments.length > 0 && (
            <span className="text-xs text-slate-500" data-testid="attachment-count">
              {pendingAttachments.length} photo{pendingAttachments.length !== 1 ? 's' : ''} attached (compressed 50%)
            </span>
          )}
        </div>

        {pendingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="pending-attachments">
            {pendingAttachments.map((att, idx) => (
              <div key={idx} className="relative group">
                <img src={att.dataUrl} alt={att.name} className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
                <button type="button" onClick={() => removeAttachment(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition" data-testid={`remove-attachment-${idx}`} aria-label="Remove attachment">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={forceInternal ? 'Add an internal note...' : 'Type your reply...'}
            className="bg-white border-slate-200 min-h-[80px] resize-none"
            data-testid="message-input"
            aria-label={forceInternal ? 'Internal note' : 'Reply message'}
          />
          <Button type="submit" className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white self-end" disabled={sending || (!newMessage.trim() && pendingAttachments.length === 0)} data-testid="send-message-btn" aria-label="Send message">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </form>
    </>
  );
}

function ActivityTimeline({ ticket, messages }) {
  // Build a simple chronological timeline from ticket fields + messages
  const events = [];
  events.push({ when: ticket.created_at, kind: 'created', text: `Ticket created by ${ticket.customer_name || 'requester'}` });
  if (ticket.assigned_agent_name) {
    events.push({ when: ticket.updated_at, kind: 'assigned', text: `Assigned to ${ticket.assigned_agent_name}` });
  }
  if (ticket.escalation_requested) {
    events.push({ when: ticket.updated_at, kind: 'escalated', text: `Elevation requested → Tier ${ticket.assignment_tier}` });
  }
  for (const m of messages || []) {
    events.push({
      when: m.created_at,
      kind: m.is_internal ? 'note' : 'reply',
      text: `${m.sender_name} ${m.is_internal ? 'added internal note' : 'replied'}: ${m.content.slice(0, 100)}${m.content.length > 100 ? '…' : ''}`,
    });
  }
  if (['resolved', 'closed'].includes(ticket.status)) {
    events.push({ when: ticket.updated_at, kind: ticket.status, text: `Ticket marked ${statusLabels[ticket.status] || ticket.status}` });
  }
  events.sort((a, b) => new Date(a.when) - new Date(b.when));

  const dot = (kind) =>
    ({
      created: 'bg-blue-500',
      assigned: 'bg-cyan-500',
      escalated: 'bg-red-500',
      reply: 'bg-slate-400',
      note: 'bg-amber-500',
      resolved: 'bg-emerald-500',
      closed: 'bg-slate-700',
    }[kind] || 'bg-slate-400');

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-slate-800">Activity Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="relative border-l border-slate-200 ml-3 space-y-5">
          {events.map((e, i) => (
            <li key={i} className="ml-4">
              <span className={`absolute -left-[7px] mt-1.5 w-3 h-3 rounded-full border-2 border-white ${dot(e.kind)}`} aria-hidden="true" />
              <p className="text-sm text-slate-800">{e.text}</p>
              <p className="text-xs text-slate-400">{formatDate(e.when)} MST · {formatRelativeTime(e.when)}</p>
            </li>
          ))}
          {events.length === 0 && <li className="ml-4 text-sm text-slate-400">No activity yet.</li>}
        </ol>
      </CardContent>
    </Card>
  );
}

function AttachmentsTab({ messages }) {
  const all = (messages || []).flatMap((m) =>
    (m.attachments || []).map((a) => ({ url: a, sender: m.sender_name, when: m.created_at, internal: m.is_internal }))
  );
  if (all.length === 0) {
    return (
      <Card className="bg-white border-slate-200">
        <CardContent className="py-12 text-center text-slate-500">
          <Paperclip className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No attachments uploaded yet.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="bg-white border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-slate-800">Attachments ({all.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {all.map((a, i) => (
            <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" className="block rounded-xl overflow-hidden border border-slate-200 hover:border-cyan-300 group">
              <img src={a.url} alt={`attachment-${i}`} className="w-full h-32 object-cover bg-slate-100" />
              <div className="p-2 text-xs">
                <p className="font-medium text-slate-700 truncate">{a.sender}</p>
                <p className="text-slate-400">{formatRelativeTime(a.when)}</p>
              </div>
            </a>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SLATab({ ticket, isAgent, onUpdate }) {
  const sla = getSLAStatus(ticket);
  // SLA due is captured/edited in MST then stored as a UTC ISO so the
  // backend remains TZ-agnostic.
  const [due, setDue] = useState(ticket.sla_due_at ? utcIsoToMstLocal(ticket.sla_due_at) : '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    try {
      setSaving(true);
      await ticketsAPI.update(ticket.id, { sla_due_at: due ? mstLocalToUtcIso(due) : null });
      onUpdate?.();
    } catch (err) {
      alert('Failed to update SLA');
    } finally {
      setSaving(false);
    }
  };

  const toneCard =
    {
      overdue: 'bg-red-50 border-red-200',
      risk: 'bg-amber-50 border-amber-200',
      on_track: 'bg-emerald-50 border-emerald-200',
      met: 'bg-slate-50 border-slate-200',
      none: 'bg-slate-50 border-slate-200',
    }[sla.state] || 'bg-slate-50 border-slate-200';

  return (
    <div className="space-y-4">
      <Card className={`${toneCard} border`}>
        <CardContent className="p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">SLA Status</p>
          <p className="text-2xl font-bold text-slate-900">{sla.label}</p>
          <p className="text-sm text-slate-600 mt-1">{ticket.sla_due_at ? `Due ${formatMst(ticket.sla_due_at)} · ${formatSLADue(ticket)}` : 'No SLA set for this ticket'}</p>
        </CardContent>
      </Card>

      {isAgent && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-slate-800">Update SLA Due Time</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col md:flex-row items-start md:items-end gap-3">
            <div className="flex-1 w-full">
              <Label htmlFor="sla-due" className="text-xs text-slate-500">SLA Due (date &amp; time, MST)</Label>
              <Input id="sla-due" type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} className="bg-white border-slate-200" data-testid="sla-due-input" />
              <p className="text-[11px] text-slate-400 mt-1">Time entered is treated as MST (UTC-7).</p>
            </div>
            <Button onClick={save} disabled={saving} className="rounded-full bg-cyan-500 hover:bg-cyan-600" data-testid="sla-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function LinkedAssetTab({ ticket, isAgent, onUpdate }) {
  const [assetId, setAssetId] = useState(ticket.linked_asset_id || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    try {
      setSaving(true);
      await ticketsAPI.update(ticket.id, { linked_asset_id: assetId || null });
      onUpdate?.();
    } catch (err) {
      alert('Failed to link asset');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card className="bg-white border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-slate-800">Linked Asset</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {ticket.linked_asset_id ? (
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase">Inventory ID</p>
            <p className="font-mono text-slate-800">{ticket.linked_asset_id}</p>
          </div>
        ) : (
          <p className="text-sm text-slate-500">No asset linked to this ticket.</p>
        )}
        {isAgent && (
          <div className="flex flex-col md:flex-row gap-2">
            <Input
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              placeholder="Inventory item ID"
              className="bg-white border-slate-200"
              data-testid="asset-id-input"
            />
            <Button onClick={save} disabled={saving} className="rounded-full bg-cyan-500 hover:bg-cyan-600" data-testid="asset-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
